import { useCallback, useEffect, useState } from "react";
import { Check, X, RotateCcw, Sparkles } from "lucide-react";
import {
  api,
  type RelationshipRevision,
  type RelationshipPending,
} from "../lib/api";
import { useRelationshipTaxonomy } from "../lib/relationship-taxonomy";

/** Surfaces the evidence ledger's continuous corrections on the
 *  Person card:
 *   - a parked PENDING proposal (user-confirmed value vs new
 *     contradicting signals) as a "Memso noticed" block with
 *     Keep / Switch / Ignore;
 *   - else a recent auto-REVISION ("Memso updated this") with the
 *     reason + Accept / Revert.
 *  Pending outranks revision (the lock case needs the user). */
export function RelationshipRevisionPill({
  personId,
  personName,
  onChanged,
}: {
  personId: string;
  personName: string;
  onChanged?: () => void;
}) {
  const taxo = useRelationshipTaxonomy();
  const [pending, setPending] = useState<RelationshipPending | null>(null);
  const [revision, setRevision] = useState<RelationshipRevision | null>(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(() => {
    api
      .relationshipPending()
      .then((d) => {
        setPending(d.pending.find((p) => p.person_id === personId) ?? null);
      })
      .catch(() => undefined);
    api
      .relationshipRevisions({ person_id: personId, unacked: true })
      .then((d) => setRevision(d.revisions[0] ?? null))
      .catch(() => undefined);
  }, [personId]);

  useEffect(() => {
    load();
  }, [load]);

  const h = (t: string | null | undefined) => taxo.humanize(t);

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      if (busy) return;
      setBusy(true);
      try {
        await fn();
        setDismissed(true);
        onChanged?.();
      } finally {
        setBusy(false);
      }
    },
    [busy, onChanged],
  );

  if (dismissed) return null;

  if (pending) {
    return (
      <div className="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50/70 dark:bg-amber-950/25 px-3 py-2.5 text-sm">
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-300 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-amber-900 dark:text-amber-100">
              Memso noticed signals that suggest {personName} might be your{" "}
              <strong>{h(pending.proposed_type)}</strong>, but you confirmed
              them as <strong>{h(pending.was_type)}</strong>. Re-confirm?
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => act(() => api.relationshipPendingAction(pending.pending_id, "keep"))}
                className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50"
              >
                Keep {h(pending.was_type)}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => act(() => api.relationshipPendingAction(pending.pending_id, "switch"))}
                className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
              >
                Switch to {h(pending.proposed_type)}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => act(() => api.relationshipPendingAction(pending.pending_id, "ignore"))}
                className="px-2.5 py-1 rounded-lg text-xs font-semibold text-slate-500 hover:text-rose-600 dark:hover:text-rose-300 disabled:opacity-50"
              >
                Ignore this evidence
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (revision) {
    return (
      <div className="rounded-xl border border-sky-300 dark:border-sky-700 bg-sky-50/70 dark:bg-sky-950/25 px-3 py-2 text-sm">
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 mt-0.5 text-sky-600 dark:text-sky-300 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sky-900 dark:text-sky-100">
              Memso updated {personName}: was{" "}
              <strong>{h(revision.was_type)}</strong>, now{" "}
              <strong>{h(revision.now_type)}</strong>.
            </p>
            {revision.reason && (
              <p className="text-xs text-sky-700/80 dark:text-sky-300/80 mt-0.5">
                {revision.reason}
              </p>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => act(() => api.relationshipRevisionAction(revision.revision_id, "accept"))}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <Check className="w-3 h-3" /> Looks right
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => act(() => api.relationshipRevisionAction(revision.revision_id, "revert"))}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50"
              >
                <RotateCcw className="w-3 h-3" /> Revert to {h(revision.was_type)}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => act(() => api.relationshipRevisionAction(revision.revision_id, "ack"))}
                aria-label="Dismiss"
                className="inline-flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 px-1 disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
